import cx from 'classnames';
import {FocusTrap} from 'focus-trap-react';
import PropTypes from 'prop-types';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {isSaveTheDateByUsedAs} from '~/common/utils/event_utils';
import {Button} from '~library/atoms/Button';
import {Checkbox} from '~library/atoms/Checkbox';
import {Divider} from '~library/atoms/Divider';
import {Select} from '~library/atoms/Select';
import {Typography} from '~library/atoms/Typography';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {feedbackInformationText} from '~libSass/base/_exports.sass';

export const EVENT_REMINDER_CHOICES = [
  ['-1', 'No Reminders'],
  ['-86400', '1 Day'],
  ['-172800', '2 Days'],
  ['-259200', '3 Days'],
  ['-345600', '4 Days'],
  ['-432000', '5 Days'],
  ['-518400', '6 Days'],
  ['-604800', '7 Days'],
  ['-691200', '8 Days'],
  ['-777600', '9 Days'],
  ['-864000', '10 Days'],
  ['-1209600', '2 Weeks'],
  ['-1814400', '3 Weeks'],
  ['-2592000', '1 Month'],
];

export const SettingsOverlay = ({
  reminders,
  notificationDefaults,
  eventDate,
  open = false,
  onClose,
  onSave,
  isHost,
}) => {
  const [notifyActivityInvolvingMe, setNotifyActivityInvolvingMe] = useState(false);
  const [notifyEventActivities, setNotifyEventActivities] = useState(false);
  const [notifyGuestRSVPs, setNotifyGuestRSVPs] = useState(false);
  const [showAddAnotherReminder, setShowAddAnotherReminder] = useState(false);
  const [enableAnotherReminder, setEnableAnotherReminder] = useState(true);

  const [reminder1, setReminder1] = useState({
    label: EVENT_REMINDER_CHOICES[0][1],
    value: EVENT_REMINDER_CHOICES[0][0],
  });
  const [reminder2, setReminder2] = useState({
    label: EVENT_REMINDER_CHOICES[0][1],
    value: EVENT_REMINDER_CHOICES[0][0],
  });

  const hasRsvpd = useSelector((store) => store.rsvp?.hasRsvpd);
  const usedAs = useSelector((state) => state?.eventDetails?.usedAs);
  const isPrivateGuestlist = useSelector(
    (state) => state?.whoIsComing?.isPrivateGuestlist ?? false
  );
  const remindersDisabled = useSelector(
    (store) => store?.eviteEvent?.options?.event_option_disable_event_reminders
  );
  const isSaveTheDate = isSaveTheDateByUsedAs(usedAs);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    setNotifyActivityInvolvingMe(!!notificationDefaults.activityInvolvingMe);
    setNotifyEventActivities(!!notificationDefaults.eventActivities);
    setNotifyGuestRSVPs(!!notificationDefaults.guestRSVPs);
    setShowAddAnotherReminder(reminders.length < 2);

    setReminder1({
      label: EVENT_REMINDER_CHOICES[0][1],
      value: EVENT_REMINDER_CHOICES[0][0],
    });
    setReminder2(undefined);
    if (remindersDisabled) {
      setReminder1({
        label: EVENT_REMINDER_CHOICES[0][1],
        value: EVENT_REMINDER_CHOICES[0][0],
      });
      setReminder2(undefined);
      setEnableAnotherReminder(false);
    } else if (reminders.length > 0) {
      let reminder = EVENT_REMINDER_CHOICES.find((c) => c[0] === reminders[0].toString());
      if (reminder) {
        setReminder1({
          label: reminder[1],
          value: reminder[0],
        });
      }
      if (reminders.length > 1) {
        reminder = EVENT_REMINDER_CHOICES.find((c) => c[0] === reminders[1].toString());
        if (reminder) {
          setReminder2({
            label: reminder[1],
            value: reminder[0],
          });
        }
      }
    } else {
      setEnableAnotherReminder(false);
    }
  }, [notificationDefaults, reminders]);

  useEffect(() => {
    if (open) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }, [open]);

  const filterForUpcomingTimes = useCallback(
    (c) => eventDate + parseInt(c[0], 10) * 1000 >= new Date().getTime(),
    [eventDate]
  );

  const reminder1Options = useMemo(
    () =>
      EVENT_REMINDER_CHOICES.filter((c) => c[0] !== reminder2?.value)
        .filter(filterForUpcomingTimes)
        .map((c) => ({label: c[1], value: c[0]})),
    [reminder2?.value, filterForUpcomingTimes]
  );

  return (
    // TODO: Use ResponsivePanel when we update View Invitation
    <>
      <div
        className={cx('view-invitation__overlay-bg', {open})}
        onClick={onClose}
        tabIndex={-1}
        onKeyUp={null}
        role="button"
        aria-label="overlay"
      />
      <FocusTrap active={open}>
        <div className={cx('view-invitation__settings-overlay', {open})}>
          <div className="settings-overlay__heading">
            <Typography variant="header5" data-qa-id="settings-header">
              Notification preferences
            </Typography>
            <Button
              data-qa-id="close-settings-overlay"
              variant="unstyled-button"
              onClick={onClose}
              ariaLabel="close settings"
            >
              <CloseLineIcon />
            </Button>
          </div>
          <Divider />
          <div className="settings-overlay__section">
            <div className="settings-overlay__section-heading">
              <Typography
                variant="label2"
                classNames="small"
                color="pebble"
                data-qa-id="notification-settings"
              >
                Notifications
              </Typography>
              <Typography variant="paragraph2">
                {isHost
                  ? 'You will receive event reminders. Get additional notifications for:'
                  : 'You will receive event reminders, cancellation notices and messages from the host(s). Select additional notifications below:'}
              </Typography>
            </div>
            <div className="settings-overlay__notification-checkboxes">
              <Checkbox
                onChange={() => {
                  if (notifyActivityInvolvingMe) {
                    setNotifyEventActivities(false);
                  }
                  setNotifyActivityInvolvingMe(!notifyActivityInvolvingMe);
                }}
                data-qa-id="notify-activity-involving-me"
                checked={notifyActivityInvolvingMe}
                label="Messages from other guests and feed activity involving you"
                disabled={!hasRsvpd || notifyEventActivities}
              />
              <div className="settings-overlay__notification-checkboxes_nested_checkbox">
                <Checkbox
                  onChange={() => {
                    if (!notifyEventActivities) {
                      setNotifyActivityInvolvingMe(true);
                    }
                    setNotifyEventActivities(!notifyEventActivities);
                  }}
                  data-qa-id="notify-event-activities"
                  checked={notifyEventActivities}
                  label="All other event activity"
                  disabled={!hasRsvpd}
                />
              </div>
              {(isHost || !isPrivateGuestlist) && !isSaveTheDate && (
                <Checkbox
                  onChange={() => setNotifyGuestRSVPs(!notifyGuestRSVPs)}
                  data-qa-id="notify-guest-rsvps"
                  checked={notifyGuestRSVPs}
                  label={isHost ? 'RSVPs from guests' : 'RSVPs from other guests'}
                  disabled={!hasRsvpd}
                />
              )}
            </div>
          </div>
          {isHost && (
            <div className="settings-overlay__section">
              <div className="settings-overlay__section-heading">
                <Typography
                  variant="label2"
                  classNames="small"
                  color="pebble"
                  data-qa-id="reminder-settings"
                >
                  Reminders
                </Typography>
                <Typography variant="paragraph2">
                  Schedule up to 2 event reminders to my guests.
                </Typography>
              </div>
              <div className="settings-overlay__reminder-section">
                <Typography variant="label2" classNames="small" color="shadow">
                  Send Reminders Before My Event
                </Typography>
                <div className="settings-overlay__reminders">
                  {reminder1Options?.length === 1
                    ? <Typography
                        variant="paragraph2"
                        size="large"
                        style={{padding: '0.5rem 0 1rem', color: feedbackInformationText}}
                      >
                        This event is in the past so you cannot change the reminders
                      </Typography>
                    : <>
                        <Select
                          options={reminder1Options}
                          data-qa-id="send-reminder-day-1"
                          value={reminder1}
                          onChange={(v) => {
                            const option = EVENT_REMINDER_CHOICES.find((o) => o[0] === v);
                            setReminder1({
                              value: option[0],
                              label: option[1],
                            });
                            if (option[0] === EVENT_REMINDER_CHOICES[0][0]) {
                              setEnableAnotherReminder(false);
                              setShowAddAnotherReminder(false);
                              setReminder2(undefined);
                            } else if (reminder2 === undefined) {
                              setEnableAnotherReminder(true);
                              setShowAddAnotherReminder(true);
                            } else {
                              setEnableAnotherReminder(true);
                              setShowAddAnotherReminder(false);
                            }
                          }}
                        />
                        {enableAnotherReminder && showAddAnotherReminder && (
                          <Button
                            data-qa-id="add-reminder"
                            variant="link"
                            size="small"
                            className="settings-overlay__add-reminder-button"
                            onClick={() => {
                              setReminder2(
                                EVENT_REMINDER_CHOICES.slice(1)
                                  .filter((c) => c[0] !== reminder1?.value)
                                  .filter(filterForUpcomingTimes)
                                  .map((c) => ({label: c[1], value: c[0]}))[0]
                              );
                              setShowAddAnotherReminder(false);
                            }}
                          >
                            Add another reminder
                          </Button>
                        )}
                        {enableAnotherReminder && !showAddAnotherReminder && (
                          <>
                            <Select
                              options={EVENT_REMINDER_CHOICES.slice(1)
                                .filter((c) => c[0] !== reminder1?.value)
                                .filter(filterForUpcomingTimes)
                                .map((c) => ({label: c[1], value: c[0]}))}
                              data-qa-id="send-reminder-day-2"
                              value={reminder2}
                              onChange={(v) => {
                                const option = EVENT_REMINDER_CHOICES.slice(1).find(
                                  (o) => o[0] === v
                                );
                                setReminder2({
                                  value: option[0],
                                  label: option[1],
                                });
                              }}
                            />
                            <Button
                              data-qa-id="remove-reminder"
                              variant="link"
                              size="small"
                              className="settings-overlay__add-reminder-button"
                              onClick={() => {
                                setShowAddAnotherReminder(true);
                                setReminder2(undefined);
                              }}
                            >
                              Remove second reminder
                            </Button>
                          </>
                        )}
                      </>}
                </div>
              </div>
            </div>
          )}
          <div>
            <style>
              {/* TODO: remove when fullWidth is fixed */}
              {`.button__full-width {
                max-width: 100% !important;
                margin-top: 1rem;
              }`}
            </style>
            <Button
              data-qa-id="save-notification-settings"
              variant="primary"
              fullWidth
              size="large"
              processingChildren="Saving..."
              className="button__full-width"
              onClick={() =>
                onSave?.(
                  {
                    activityInvolvingMe: notifyActivityInvolvingMe,
                    eventActivities: notifyEventActivities,
                    // Only allow guest RSVP notifications if user is host or if guest list is not private
                    // If not host AND guest list is private, force to false
                    guestRSVPs: isHost || !isPrivateGuestlist ? notifyGuestRSVPs : false,
                  },
                  [
                    reminder1?.value ? parseInt(reminder1.value, 10) : null,
                    reminder2?.value ? parseInt(reminder2.value, 10) : null,
                  ]
                )
              }
              data-impression-tracking="true"
            >
              Save
            </Button>
          </div>
        </div>
      </FocusTrap>
    </>
  );
};

SettingsOverlay.propTypes = {
  notificationDefaults: PropTypes.shape({
    activityInvolvingMe: PropTypes.bool,
    eventActivities: PropTypes.bool,
    guestRSVPs: PropTypes.bool,
  }).isRequired,
  isHost: PropTypes.bool.isRequired,
  reminders: PropTypes.arrayOf(PropTypes.number).isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  eventDate: PropTypes.number.isRequired,
};
